// Mirror of backend's `Permission` enum (`backend/src/types/enums.ts`).
// Keep these strings in sync — the backend treats them as the canonical
// values, and they appear in /v1/auth/me's `permissions` array.
export const Permission = {
  InviteAdmin: "invite_admin",
  ConnectPlatform: "connect_platform",
  ManageBilling: "manage_billing",
  ManageProducts: "manage_products",
  ManageOrders: "manage_orders",
  ManageAiBot: "manage_ai_bot",
} as const;

export type Permission = (typeof Permission)[keyof typeof Permission];

export interface PermissionRow {
  permission: Permission;
  labelKey: string;
  descriptionKey: string;
  /** Role names (lowercase) whose base permissions already include this —
   *  the modal shows a locked-checked switch when the member has one of
   *  these roles. */
  roleBaseFor: readonly string[];
}

/**
 * Canonical render order + copy for the member permissions modal.
 * Adding a 6th permission later is one entry here.
 */
export const PERMISSION_CATALOG: readonly PermissionRow[] = [
  {
    permission: Permission.ManageProducts,
    labelKey: "permissions.manageProducts.label",
    descriptionKey: "permissions.manageProducts.description",
    roleBaseFor: ["admin", "owner"],
  },
  {
    permission: Permission.ManageOrders,
    labelKey: "permissions.manageOrders.label",
    descriptionKey: "permissions.manageOrders.description",
    roleBaseFor: ["admin", "owner"],
  },
  {
    permission: Permission.ConnectPlatform,
    labelKey: "permissions.connectPlatform.label",
    descriptionKey: "permissions.connectPlatform.description",
    roleBaseFor: ["owner"],
  },
  {
    permission: Permission.InviteAdmin,
    labelKey: "permissions.inviteAdmin.label",
    descriptionKey: "permissions.inviteAdmin.description",
    roleBaseFor: ["owner"],
  },
  {
    permission: Permission.ManageBilling,
    labelKey: "permissions.manageBilling.label",
    descriptionKey: "permissions.manageBilling.description",
    roleBaseFor: ["owner"],
  },
  {
    permission: Permission.ManageAiBot,
    labelKey: "permissions.manageAiBot.label",
    descriptionKey: "permissions.manageAiBot.description",
    roleBaseFor: ["owner"],
  },
];

/** Is this permission part of the role's base grants (always-on)? */
export function isRoleBasePermission(row: PermissionRow, memberRole: string | null): boolean {
  if (memberRole === null) return false;
  return row.roleBaseFor.includes(memberRole);
}
